// ───────────────────────────────────────────────────────────────────────────────
//  ActInfo.jsx  (main panel for an Act) with auto-navigation to specific metrics
// ───────────────────────────────────────────────────────────────────────────────
import React, { useState, useMemo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  CheckCircle, Star, AlertCircle, Flag, Calendar,
  ChevronDown, ChevronUp
} from 'lucide-react';

import { ACT_CATEGORIES } from './ScoreCategories';
import { METRIC_KEYS } from '../Constants';
import PolarChart from './PolarChart';
import ItemList from './ItemList';
import ScoreCategorySection from './ScoreCategorySection';
import Tabs from './Tabs';
import InfoHeader from './InfoHeader';

import './styles/info-panel.css';
import '../grades.css';

// Helper function to find which category contains a specific metric
const findCategoryForMetric = (metricKey, categories) => {
  // Search through all categories to find which one contains the metric
  for (const [categoryName, items] of Object.entries(categories)) {
    const found = items.find(([key, label]) => key === metricKey);
    if (found) {
      return categoryName;
    }
  }
  return null;
};

// ────────────────────────────────────
// Re‑usable simple card (no collapse)
// ────────────────────────────────────
const Card = ({ title, children }) => (
  <div className="border rounded p-3 mb-3 bg-white shadow-sm">
    <h6 className="fw-semibold mb-2">{title}</h6>
    {children}
  </div>
);

Card.propTypes = { title:PropTypes.node, children:PropTypes.node };

// ────────────────────────────────────
// Vertical timeline milestone card
// ────────────────────────────────────
const MilestoneCard = ({ label, data }) => {
  if (!data) return null;
  const rounded = Number.isInteger(data.score)
    ? data.score
    : parseFloat(data.score).toFixed(1);
  return (
    <div className="d-flex mb-3">
      <div className="me-3 d-flex flex-column align-items-center">
        <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center" style={{width:32,height:32}}>
          <Calendar size={16} />
        </div>
        <div className="border-start border-2 flex-grow-1" />
      </div>
      <div>
        <h6 className="mb-1">
          {label}
          <span className={`badge grade-${Math.round(data.score)} ms-2`} style={{color:'#000'}}>{rounded}/10</span>
        </h6>
        <p className="mb-0 small"><em>{data.explanation}</em></p>
      </div>
    </div>
  );
};

MilestoneCard.propTypes = {
  label:PropTypes.string,
  data:PropTypes.shape({ score:PropTypes.oneOfType([PropTypes.number,PropTypes.string]), explanation:PropTypes.string })
};

// ────────────────────────────────────
// Diagnostics helper
// ────────────────────────────────────
const DiagnosticsList = ({ giveaways, blindSpots, pacing }) => (
  <>
    {pacing && <Card title="Pacing Note"><p className="mb-0">{pacing.explanation}</p></Card>}
    {blindSpots?.length > 0 && <Card title="Blind Spots"><ItemList items={blindSpots} /></Card>}
    {giveaways?.length > 0 && <Card title="Amateur Giveaways"><ItemList items={giveaways} /></Card>}
  </>
);

DiagnosticsList.propTypes = {
  giveaways:PropTypes.array, blindSpots:PropTypes.array, pacing:PropTypes.object
};

// ────────────────────────────────────
// Main component
// ────────────────────────────────────
export default function ActInfo({ data }) {
  const analysis = data?.analysis || {};
  const scores   = data?.scores   || {};
  const summary  = data?.summary  || '';
  const selectedMetric = data?.selectedMetric; // NEW: Get the selected metric

  const [activeTab, setActiveTab] = useState('summary');
  const [expandAll, setExpandAll] = useState(false);
  const categoryRefs = useRef({}); // NEW: Refs for each category section

  // Auto-navigate when a specific metric is selected
// Auto-navigate when a specific metric is selected
useEffect(() => {
  console.log('ActInfo useEffect triggered:', { selectedMetric });
  
  if (selectedMetric && selectedMetric !== 'overall') {
    console.log('Processing metric:', selectedMetric);
    
    // Switch to scores tab
    setActiveTab('scores');
    // Expand all sections
    setExpandAll(true);
    
    // Find the actual metric key used in acts
    const metricKey = METRIC_KEYS[selectedMetric]?.act;
    console.log('Act metric key:', metricKey);
    
    setTimeout(() => {
      // Find the specific score card by its metric key
      const targetCard = document.querySelector(`[data-metric-key="${metricKey}"]`);
      console.log('Found target card:', targetCard);
      
      if (targetCard) {
        targetCard.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' // Center the card in view
        });
        
        // Add a subtle highlight pulse to the specific card
        targetCard.classList.add('metric-target-pulse');
        setTimeout(() => {
          targetCard.classList.remove('metric-target-pulse');
        }, 3000);
      } else {
        console.log('Could not find target card for metric key:', metricKey);
      }
    }, 500);
  }
}, [selectedMetric, data.id]);

  // category averages for colored badge in header
  const categoryMeans = useMemo(() => {
    const m = {};
    Object.entries(ACT_CATEGORIES).forEach(([cat, items]) => {
      const vals = items.map(([k])=>parseFloat(scores[k]?.score)).filter(n=>!isNaN(n));
      m[cat] = vals.length ? (vals.reduce((a,b)=>a+b,0)/vals.length).toFixed(1) : null;
    });
    return m;
  }, [scores]);

  // Tabs
  const tabs = [
    { key:'summary',    label:'Summary',            icon:<CheckCircle className="tab-icon-svg"/> },
    { key:'scores',     label:'Scores',             icon:<Star className="tab-icon-svg"/> },
    { key:'milestones', label:'Milestones',         icon:<Calendar className="tab-icon-svg"/> },
    { key:'strengths',  label:'Strengths & Fixes',  icon:<CheckCircle className="tab-icon-svg"/> },
    { key:'diag',       label:'Diagnostics',        icon:<AlertCircle className="tab-icon-svg"/> }
  ];

  // ---------- Summary ----------
  const SummaryTab = () => (
    <div className="tab-content">
      {analysis.dramaticQuestion?.primary && <Card title="Dramatic Question"><p className="mb-0">{analysis.dramaticQuestion.primary}</p></Card>}
      {summary && <Card title="Summary"><p className="mb-0">{summary}</p></Card>}
      {analysis.executiveSummary && <Card title="Executive Summary"><p className="mb-0">{analysis.executiveSummary}</p></Card>}
      {analysis.purpose && <Card title="Act Purpose"><p className="mb-0">{analysis.purpose}</p></Card>}
      {analysis.coreConcept && (
        <Card title={<>
          Core Concept <span className={`badge grade-${Math.round(analysis.coreConcept.score)} ms-2`} style={{color:'#000'}}>
{
  (() => {
    const score = parseFloat(analysis.coreConcept.score);
    if (isNaN(score)) return 'N/A';
    return Number.isInteger(score) ? score.toFixed(0) : score.toFixed(1);
  })()
}/10
          </span>
        </>}>
          <p className="mb-1"><strong>Idea:</strong> {analysis.coreConcept.identification}</p>
          {analysis.coreConcept.explanation && <p className="mb-2 small"><em>{analysis.coreConcept.explanation}</em></p>}
          {analysis.coreConcept.improvementSuggestions?.length > 0 && (
            <ul className="small mb-0">
              {analysis.coreConcept.improvementSuggestions.map((s,i)=><li key={i}>{s}</li>)}
            </ul>
          )}
        </Card>) }
    </div>
  );

  // ---------- Scores ----------
  const ScoresTab = () => (
    <div className="tab-content">
      <Card title="At‑a‑Glance Score Shape">
        <PolarChart
          scores={scores}
          axes={[
            {key:'impact',label:'Impact'},
            {key:'plotProgression',label:'Plot'},
            {key:'stakesHandling',label:'Stakes'},
            {key:'pacing',label:'Pacing'},
            {key:'character',label:'Character'},
            {key:'originality',label:'Originality'},
            {key:'emotionalImpact',label:'Emotion'},
            {key:'readability',label:'Readability'}
          ]}
        />
      </Card>
      <button className="btn btn-sm btn-outline-secondary mb-2" onClick={()=>setExpandAll(p=>!p)}>
        {expandAll ? <><ChevronUp size={14}/> Collapse All</> : <><ChevronDown size={14}/> Expand All</>}
      </button>

      {Object.entries(ACT_CATEGORIES).map(([cat, items]) => (
        <div 
          key={cat} 
          className="mb-4" 
          ref={el => categoryRefs.current[cat] = el}
        >
          <h5 className="fw-semibold">
            {cat}
            {categoryMeans[cat] && <span className={`badge grade-${Math.round(categoryMeans[cat])} ms-2`} style={{color:'#000'}}>{categoryMeans[cat]}</span>}
          </h5>

<ScoreCategorySection 
  items={items} 
  scores={scores} 
  expandAll={expandAll}
  highlightedMetric={selectedMetric}
  nodeType="act"
/> 
       </div>
      ))}

      <button className="btn btn-sm btn-outline-secondary mt-2" onClick={()=>setExpandAll(p=>!p)}>
        {expandAll ? <><ChevronUp size={14}/> Collapse All</> : <><ChevronDown size={14}/> Expand All</>}
      </button>
    </div>
  );

  // ---------- Milestones ----------
  const MilestonesTab = () => (
    <div className="tab-content">
      <MilestoneCard label="Opening Beat"  data={scores.entryBeat} />
      <MilestoneCard label="Midpoint/Turn" data={scores.midpointOrMajorTurn} />
      <MilestoneCard label="Act Climax"    data={scores.actClimax} />
    </div>
  );

  // ---------- Strength/Fixes ----------
  const StrengthsTab = () => (
    <div className="tab-content">
      {analysis.strengthsToPreserve?.length>0 && <Card title="Strengths to Preserve"><ItemList items={analysis.strengthsToPreserve} /></Card>}
      {analysis.priorityFixes?.length>0 && <Card title="Priority Fixes"><ItemList items={analysis.priorityFixes} /></Card>}
    </div>
  );

  // ---------- Diagnostics ----------
  const DiagnosticTab = () => (
    <div className="tab-content">
      <DiagnosticsList giveaways={analysis.amateur_giveaways} blindSpots={analysis.blind_spots} pacing={scores.pacing} />
    </div>
  );

  
  const Panel = { summary:SummaryTab, scores:ScoresTab, milestones:MilestonesTab, strengths:StrengthsTab, diag:DiagnosticTab }[activeTab];

  return (
    <div className="info-panel">
      <InfoHeader title={data.title} id={data.id} execution={data.execution} />
      <div className="info-body">
        <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />
        {Panel && <Panel />}
      </div>
    </div>
  );
}

ActInfo.propTypes = {
  data:PropTypes.shape({
    title:PropTypes.string,
    id:PropTypes.oneOfType([PropTypes.string,PropTypes.number]),
    design:PropTypes.number,
    execution:PropTypes.number,
    summary:PropTypes.string,
    analysis:PropTypes.object,
    scores:PropTypes.object,
    selectedMetric:PropTypes.string // NEW
  }).isRequired
};